import { post } from '@creator/sdk/network/api-service';
import type { OutputBlockData } from '@editorjs/editorjs';
import AdvertisingSlot from '@src/components/Ads/AdvertisingSlot';
import { isResponsePost, parsePostContent } from '@src/model/upvote/helpers';
import { usePost } from '@src/store/posts/hooks/usePost';
import { getTextBasedBlockContent, isParagraphBasedBlockType } from '@src/utils/editorjs-utils/editorjs-utils';
import { propsEqualityFn } from '@src/utils/object-utils/object-utils';
import { isMobile } from '@src/utils/utils';
import { Fragment, memo, useMemo } from 'react';
import type { FC } from 'react';
import EditorViewOnlyAudioTool from './AudioTool/AudioTool';
import EditorViewOnlyDelimiterTool from './DelimiterTool/DelimiterTool';
import EditorViewOnlyEmbedTool from './EmbedTool/EmbedTool';
import EditorViewOnlyHeaderTool from './HeaderTool/HeaderTool';
import EditorViewOnlyImageTool from './ImageTool/ImageTool';
import EditorViewOnlyLinkTool from './LinkTool/LinkTool';
import EditorViewOnlyListTool from './ListTool/ListTool';
import EditorViewOnlyParagraphTool from './ParagraphTool/ParagraphTool';
import EditorViewOnlyQuoteTool from './QuoteTool/QuoteTool';
import EditorViewOnlyVideoTool from './VideoTool/VideoTool';
import EditorViewOnlyWarningTool from './WarningTool/WarningTool';

export interface EditorViewOnlyProps {
    postId: string;
    className?: string;
    tokenName: string;
    id: string;
}

const EditorViewOnly: FC<EditorViewOnlyProps> = props => {
    const { className, tokenName, postId, id } = props;

    const post = usePost(postId);
    const postContent = useMemo(() => post && parsePostContent(post), [post]);

    function renderBlockContent(type: string, blockData: OutputBlockData['data']) {
        if (type === 'paragraph') return <EditorViewOnlyParagraphTool blockData={blockData} />;
        if (type === 'linkTool') return <EditorViewOnlyLinkTool blockData={blockData} />;
        if (type === 'image' || type === 'simpleImage') return <EditorViewOnlyImageTool blockData={blockData} />;
        if (type === 'video' && blockData.url)
            return <EditorViewOnlyVideoTool postId={postId} blockData={blockData} tokenName={tokenName} showFullScreenButton={isResponsePost(postId)} />;

        if (type === 'audio' && blockData.url) return <EditorViewOnlyAudioTool postId={postId} blockData={blockData} showFullScreenButton={isResponsePost(postId)} />;

        if (type === 'header') return <EditorViewOnlyHeaderTool blockData={blockData} />;
        if (type === 'list') return <EditorViewOnlyListTool blockData={blockData} />;
        if (type === 'delimiter') return <EditorViewOnlyDelimiterTool blockData={blockData} />;
        if (type === 'embed') return <EditorViewOnlyEmbedTool blockData={blockData} />;
        if (type === 'warning') return <EditorViewOnlyWarningTool blockData={blockData} />;
        if (type === 'quote') return <EditorViewOnlyQuoteTool blockData={blockData} />;

        return <div />;
    }

    function renderAd(index: number) {
        const { tokenName } = props;
        const location = isMobile() ? 'mid_center' : 'mid_center_card';
        return <AdvertisingSlot divIdSufix={index.toString()} location={location} tokenName={tokenName} />;
    }

    function renderBlock(block: OutputBlockData<string, any>, i: number) {
        return (
            <div key={block.id || i} className="ce-block">
                <div className="ce-block__content">{renderBlockContent(block.type, block.data)}</div>
            </div>
        );
    }

    function renderBlocks() {
        if (!postContent) return null;
        let textBlockCount = 0;
        let totalCharCount = 0;
        let lastAdIndex = -1;
        let adIndex = 0;

        return postContent.blocks.map((block, i) => {
            const isTextBlock = isParagraphBasedBlockType(block.type);
            if (isTextBlock) {
                textBlockCount++;
                totalCharCount += getTextBasedBlockContent(block).length;
            }

            if (isTextBlock && textBlockCount % 3 === 0 && totalCharCount - lastAdIndex >= 500) {
                lastAdIndex = totalCharCount;
                adIndex = adIndex + 1;
                return (
                    <Fragment key={block.id}>
                        {renderBlock(block, i)}
                        {renderAd(adIndex)}
                    </Fragment>
                );
            }

            return renderBlock(block, i);
        });
    }

    return (
        <div id={id} className={`editorjs-container ${className}`}>
            {renderBlocks()}
        </div>
    );
};

export default memo(EditorViewOnly, propsEqualityFn);
